import React from "react";
import { Box, Inline, Text, MenuRenderer, MenuItem, IconChevron, Drawer, Stack, MenuItemDivider, IconTick } from "braid-design-system";
import { useTranslation } from 'react-i18next';
import { useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import "../styles/MenuBar.css";
import CashierSignInOff from "./CashierSignInOff";
// import { fetchUserEnabledReports } from "../services/menuService";
import { useToast } from "../hooks/useToast";
import { useUserPermissionAndMenu } from "../contexts/UserPermissionAndMenuProvider";
import { useAuth } from "../contexts/AuthProvider";
import { useWebSocket } from "../contexts/WebSocketProvider";
import { WEBSOCKET_MESSAGE_TYPES } from "../constants/webSocketTypes";
import { WebSocketResponse } from "../schemas/webSocketResponseSchema";
import { getChildMenus } from "../utils/dynamicRouteLoader";
import { unifiedTransactionService } from "../services/unifiedTransactionService";
import TransactionApprovalDrawer from "./TransactionApprovalDrawer";

// Type definition for menu items
interface MenuItemType {
    label: string;
    to: string;
    children?: MenuItemType[];
    isDivider?: boolean;
}

let globalApprovalCountsCache: Record<string, { pending: number; approved: number; rejected: number }> | null = null;
let globalApprovalCountsPromise: Promise<Record<string, { pending: number; approved: number; rejected: number }>> | null = null;

const MenuBar: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const { userMenus, userPermissions, loading } = useUserPermissionAndMenu();
    const [menuItems, setMenuItems] = React.useState<MenuItemType[]>([]);
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    // Approval transaction drawer state
    const [approvalCounts, setApprovalCounts] = React.useState<Record<string, { pending: number; approved: number; rejected: number }>>({});
    const [drawerType, setDrawerType] = React.useState<string | null>(null);
    const [openSubMenus, setOpenSubMenus] = React.useState<Record<string, boolean>>({});

    // Check if user has REPORT:VIEW permission
    const hasReportViewPermission = React.useMemo(() => {
        return userPermissions?.some(perm => perm.permission === "REPORT:VIEW") ?? false;
    }, [userPermissions]);

    React.useEffect(() => {
        if (loading || !userMenus || userMenus.length === 0) {
            return;
        }

        // Build menu structure from backend menus
        // Get all unique parent menus
        const parentIds = new Set(userMenus.map(m => m.parent_id).filter(id => id !== null && id !== undefined));

        // Root menus are those without a parent OR those not in the parent list
        const rootMenus = userMenus
            .filter(menu =>
                (!menu.parent_id || !parentIds.has(menu.parent_id as number)) &&
                menu.has_menu === true
            )
            // Ensure semantic numeric ordering for dotted order numbers like `1.1`, `1.11`, `1.2`.
            // We split on dots and compare integer segments so that [1,2] < [1,11].
            .sort((a, b) => {
                const segs = (v: any) => String(v ?? '0').split('.').map((s: string) => {
                    const n = parseInt(s.replace(/[^0-9]+/g, ''), 10);
                    return Number.isNaN(n) ? 0 : n;
                });
                const sa = segs(a.order_num);
                const sb = segs(b.order_num);
                const len = Math.max(sa.length, sb.length);
                for (let i = 0; i < len; i++) {
                    const va = sa[i] ?? 0;
                    const vb = sb[i] ?? 0;
                    if (va !== vb) return va - vb;
                }
                return 0;
            });

        const builtMenuItems: MenuItemType[] = rootMenus
            .map((menu): MenuItemType | null => {
                const children = getChildMenus(menu.id as number, userMenus)
                    .filter(child => child.has_menu === true)
                    .sort((a, b) => {
                        const segs = (v: any) => String(v ?? '0').split('.').map((s: string) => {
                            const n = parseInt(s.replace(/[^0-9]+/g, ''), 10);
                            return Number.isNaN(n) ? 0 : n;
                        });
                        const sa = segs(a.order_num);
                        const sb = segs(b.order_num);
                        const len = Math.max(sa.length, sb.length);
                        for (let i = 0; i < len; i++) {
                            const va = sa[i] ?? 0;
                            const vb = sb[i] ?? 0;
                            if (va !== vb) return va - vb;
                        }
                        return 0;
                    });

                // Only include menu if it has a valid route OR has children
                if (!menu.to_url && children.length === 0) {
                    return null;
                }

                return {
                    label: menu.label || '',
                    to: menu.to_url || '#',
                    children: children.length > 0
                        ? children
                            .map(child => ({
                                label: child.label || '',
                                to: child.to_url || '#',
                            }))
                        : undefined
                };
            })
            .filter((item): item is MenuItemType => item !== null);

        setMenuItems([...builtMenuItems]);

    }, [userMenus, loading, toast, hasReportViewPermission, user?.id]);

    const fetchCounts = React.useCallback(async (force = false) => {
        try {
            if (force) {
                globalApprovalCountsCache = null;
                globalApprovalCountsPromise = null;
            }
            if (!force && globalApprovalCountsCache) {
                setApprovalCounts(globalApprovalCountsCache);
                return;
            }
            if (globalApprovalCountsPromise) {
                const counts = await globalApprovalCountsPromise;
                setApprovalCounts(counts || {});
                return;
            }
            globalApprovalCountsPromise = unifiedTransactionService.getApprovalCounts();
            const counts = await globalApprovalCountsPromise;
            globalApprovalCountsCache = counts || {};
            globalApprovalCountsPromise = null;
            setApprovalCounts(globalApprovalCountsCache);
        } catch {
            globalApprovalCountsPromise = null;
            // ignore
        }
    }, []);

    React.useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    const { onMessage, offMessage } = useWebSocket();
    React.useEffect(() => {
        if (!onMessage || !offMessage) return;
        const handleMessage = (message: WebSocketResponse<any>) => {
            if (message.type !== WEBSOCKET_MESSAGE_TYPES.TRANSACTION_APPROVAL_PENDING) return;
            fetchCounts(true);
        };
        onMessage(handleMessage);
        return () => offMessage(handleMessage);
    }, [onMessage, offMessage, fetchCounts]);

    React.useEffect(() => {
        const handleRefresh = () => {
            fetchCounts(true);
        };
        window.addEventListener('transaction-completed', handleRefresh);
        window.addEventListener('transaction-submitted', handleRefresh);
        return () => {
            window.removeEventListener('transaction-completed', handleRefresh);
            window.removeEventListener('transaction-submitted', handleRefresh);
        };
    }, [fetchCounts]);

    const handleMenuItemClick = (to: string) => {
        navigate(to);
        setDrawerOpen(false);
    };

    const isPathActive = (itemTo: string) => {
        if (!itemTo || itemTo === "#") return false;
        if (itemTo === "/") {
            return location.pathname === "/";
        }
        return location.pathname.startsWith(itemTo) || location.pathname.includes(itemTo);
    };

    const toggleSubMenu = (key: string) => {
        setOpenSubMenus(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const renderMenuItemsForDrawer = (items: MenuItemType[], depth = 0) => {
        return items.map((item, index) => {
            const itemKey = `depth-${depth}-${item.label || item.to || index}`;
            const isOpen = !!openSubMenus[itemKey];

            if (item.children) {
                return (
                    <Box key={itemKey} display="flex" flexDirection="column">
                        <Box
                            paddingY="small"
                            paddingX="medium"
                            display="flex"
                            alignItems="center"
                            cursor="pointer"
                            onClick={() => toggleSubMenu(itemKey)}
                            style={{ 
                                borderBottom: '1px solid #f1f5f9',
                                background: isOpen ? '#f8fafc' : 'transparent',
                                paddingLeft: `${16 + depth * 16}px`
                            }}
                        >
                            <Box flexGrow={1} display="flex" alignItems="center">
                                <Text
                                    tone={isPathActive(item.to) ? "brandAccent" : "secondary"}
                                    weight={isPathActive(item.to) ? "strong" : "regular"}
                                >
                                    {t(
                                        `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                        { defaultValue: item.label }
                                    )}
                                </Text>
                            </Box>
                            <IconChevron direction={isOpen ? "up" : "down"} />
                        </Box>
                        
                        {isOpen && (
                            <Box display="flex" flexDirection="column" style={{ background: '#fcfdfe' }}>
                                {renderMenuItemsForDrawer(item.children, depth + 1)}
                            </Box>
                        )}
                    </Box>
                );
            }

            if (item.isDivider) {
                return <Box key={itemKey} style={{ height: '1px', backgroundColor: '#e2e8f0', margin: '4px 0' }} />;
            }

            return (
                <Box
                    key={itemKey}
                    paddingY="small"
                    paddingX="medium"
                    cursor="pointer"
                    onClick={() => handleMenuItemClick(item.to || '')}
                    style={{ 
                        borderBottom: '1px solid #f1f5f9',
                        paddingLeft: `${16 + depth * 16}px`,
                        background: location.pathname === item.to ? '#eff6ff' : 'transparent'
                    }}
                >
                    <RouterLink to={item.to || ''} style={{ textDecoration: "none", display: 'flex', width: '100%', alignItems: 'center' }}>
                        <Text
                            tone={location.pathname === item.to ? "brandAccent" : "secondary"}
                            weight={location.pathname === item.to ? "strong" : "regular"}
                        >
                            {t(
                                `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                { defaultValue: item.label }
                            )}
                        </Text>
                    </RouterLink>
                </Box>
            );
        });
    };

    const renderBadgeIcon = (type: 'pending' | 'approved' | 'rejected', count: number) => {
        const iconColor = type === 'pending' ? '#fbbf24' : type === 'approved' ? '#34d399' : '#f87171';
        const badgeClass = type === 'pending' ? 'premium-menu-badge-pending' : type === 'approved' ? 'premium-menu-badge-approved' : 'premium-menu-badge-rejected';
        const tooltipText = type === 'pending' ? 'Pending Approvals' : type === 'approved' ? 'Approved Transactions' : 'Rejected Transactions';

        const getIconSvg = () => {
            if (type === 'pending') {
                return (
                    <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg" style={{ color: iconColor }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <circle cx="10" cy="15" r="3"></circle>
                        <polyline points="10 13 10 15 11.5 16"></polyline>
                    </svg>
                );
            }
            if (type === 'approved') {
                return (
                    <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg" style={{ color: iconColor }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <polyline points="9 15 11 17 15 13"></polyline>
                    </svg>
                );
            }
            return (
                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg" style={{ color: iconColor }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="9.5" y1="13.5" x2="14.5" y2="18.5"></line>
                    <line x1="14.5" y1="13.5" x2="9.5" y2="18.5"></line>
                </svg>
            );
        };

        return (
            <div 
                className="premium-menu-badge-container" 
                onClick={() => setDrawerType('all')}
                title={tooltipText}
                key={type}
            >
                {getIconSvg()}
                {count > 0 && (
                    <span className={`premium-menu-badge ${badgeClass}`}>
                        {count}
                    </span>
                )}
            </div>
        );
    };

    return (
        <Box paddingY="xxsmall" paddingX="xsmall" paddingLeft={"large"} className="premium-menubar">
            <style>{`
                @media (max-width: 1215px) {
                    .menu-drawer-trigger { display: block; }
                    .menu-horizontal { display: none; }
                }
                @media (min-width: 1216px) {
                    .menu-drawer-trigger { display: none; }
                    .menu-horizontal { display: block; }
                }
            `}</style>

            <div className="menu-drawer-trigger">
                <Inline space="small" alignY="center">
                    <Box
                        cursor="pointer"
                        onClick={() => setDrawerOpen(true)}
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            width: '36px', 
                            height: '36px', 
                            borderRadius: '50%', 
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        <svg stroke="currentColor" fill="none" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg" style={{ color: '#ffffff' }}>
                            <line x1="3" y1="12" x2="21" y2="12"></line>
                            <line x1="3" y1="6" x2="21" y2="6"></line>
                            <line x1="3" y1="18" x2="21" y2="18"></line>
                        </svg>
                    </Box>

                    <Box flexGrow={1} />

                    <Inline space="medium" alignY="center">
                        {/* Approval badges — stay in menu bar on mobile */}
                        {(function () {
                            const pendingCount = Object.values(approvalCounts).reduce(
                                (s, c) => s + (c.pending || 0), 0
                            );
                            const approvedCount = Object.values(approvalCounts).reduce(
                                (s, c) => s + (c.approved || 0), 0
                            );
                            const rejectedCount = Object.values(approvalCounts).reduce(
                                (s, c) => s + (c.rejected || 0), 0
                            );
                            return (
                                <Inline space="small" alignY="center">
                                    {renderBadgeIcon('pending', pendingCount)}
                                    {renderBadgeIcon('approved', approvedCount)}
                                    {renderBadgeIcon('rejected', rejectedCount)}
                                </Inline>
                            );
                        })()}

                        <CashierSignInOff />
                    </Inline>
                </Inline>

                <Drawer
                    title="Menu"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                >
                    <Stack space="none">
                        {renderMenuItemsForDrawer(menuItems)}
                    </Stack>
                </Drawer>
            </div>

            <div className="menu-horizontal">
                <Inline space="none" alignY="center">
                    {menuItems.map((item, index) =>
                        item.children ? (
                            <MenuRenderer
                                key={`${item.to}-${index}`}
                                width="large"
                                trigger={(triggerProps, { open }) => (
                                    <Box
                                        paddingY={"xxsmall"}
                                        className={`menu-link ${isPathActive(item.to) ? "menu-active" : ""}`}
                                        cursor="pointer"
                                        {...triggerProps}
                                    >
                                        <Text
                                            weight={isPathActive(item.to) ? "strong" : "regular"}
                                            size="small"
                                        >
                                            {t(
                                                `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                { defaultValue: item.label }
                                            )}
                                            <IconChevron direction={open ? "up" : "down"} />
                                        </Text>
                                    </Box>
                                )}
                            >
                                {item.children.map((child, childIndex) => (
                                    child.isDivider ? (
                                        <MenuItemDivider key={`divider-${childIndex}`} />
                                    ) : child.children ? (
                                        <MenuRenderer
                                            key={`${child.to}-${childIndex}`}
                                            width="large"
                                            trigger={(triggerProps, { open }) => (
                                                <Box
                                                    paddingX="small"
                                                    paddingY="xxsmall"
                                                    display="flex"
                                                    alignItems="center"
                                                    cursor="pointer"
                                                    {...triggerProps}
                                                >
                                                    <Text size="small">
                                                        {t(
                                                            `menu.${(child.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                            { defaultValue: child.label }
                                                        )}
                                                        <IconChevron direction={open ? "up" : "down"} />
                                                    </Text>
                                                </Box>
                                            )}
                                        >
                                            {child.children.map((report, reportIndex) => (
                                                <MenuItem
                                                    key={`${report.to}-${reportIndex}`}
                                                    onClick={() => navigate(report.to)}
                                                    icon={location.pathname === report.to ? <IconTick /> : undefined}
                                                >
                                                    {t(
                                                        `menu.${(report.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                        { defaultValue: report.label }
                                                    )}
                                                </MenuItem>
                                            ))}
                                        </MenuRenderer>
                                    ) : (
                                        <MenuItem
                                            key={`${child.to}-${childIndex}`}
                                            onClick={() => navigate(child.to)}
                                            icon={location.pathname === child.to ? <IconTick /> : undefined}
                                        >
                                            {t(
                                                `menu.${(child.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                                { defaultValue: child.label }
                                            )}
                                        </MenuItem>
                                    )
                                ))}
                            </MenuRenderer>
                        ) : (
                             <Box
                                 key={`${item.to}-${index}`}
                                 paddingY={"xxsmall"}
                                 className={`menu-link ${isPathActive(item.to) ? "menu-active" : ""}`}
                                 cursor="pointer"
                                 onClick={() => navigate(item.to)}
                             >
                                 <Text
                                     weight={isPathActive(item.to) ? "strong" : "regular"}
                                     size="small"
                                 >
                                    {t(
                                        `menu.${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
                                        { defaultValue: item.label }
                                    )}
                                </Text>
                            </Box>
                        )
                    )}
                    <Box flexGrow={1} />

                    {/* Approval badges — show pending | approved | rejected counts */}
                    <Inline space="medium" alignY="center">
                        {(function () {
                            const pendingCount = Object.values(approvalCounts).reduce(
                                (s, c) => s + (c.pending || 0), 0
                            );
                            const approvedCount = Object.values(approvalCounts).reduce(
                                (s, c) => s + (c.approved || 0), 0
                            );
                            const rejectedCount = Object.values(approvalCounts).reduce(
                                (s, c) => s + (c.rejected || 0), 0
                            );
                            return (
                                <Inline space="small" alignY="center">
                                    {renderBadgeIcon('pending', pendingCount)}
                                    {renderBadgeIcon('approved', approvedCount)}
                                    {renderBadgeIcon('rejected', rejectedCount)}
                                </Inline>
                            );
                        })()}

                        <CashierSignInOff />
                    </Inline>
                </Inline>
            </div>

            {/* Transaction Approval Drawer */}
            {drawerType && (
                <TransactionApprovalDrawer
                    transactionType={drawerType}
                    open={!!drawerType}
                    onClose={() => setDrawerType(null)}
                    onRefreshCounts={fetchCounts}
                />
            )}
        </Box>
    );
};

export default MenuBar;